import i18n from '@/i18n';
import { format } from 'date-fns';
import { getImageFromIndexedDB } from '@/utils/imageStorage';
import { imageSpaceAPI, cropImageAPI, templateAPI, compositeImageAPI } from '@/utils/api';
import { compressImageForDatabase, ensureImageSizeWithinLimit } from '@/utils/imageUpload';
import {
  buildTemplateSlideUrlMap,
  normalizeGoodsImageRefToId,
  normalizeGoodsImageRefForCompose,
  inlineUrlFromGoodsImageRef,
  resolveUrlFromImageSpaceRecord,
  resolveDrawableImageUrl,
  expandImageGroupForStampAreas,
  resolveStampAreasForSlide,
} from './goodsTemplateImageUrls';
import { compositeStampOntoTemplateDataUrl } from './goodsCanvasComposite';
import {
  compositeFabricSlideToDataUrl,
  getIpeLayersForSlide,
  slideUsesFabricComposite,
} from './fabricCanvasComposite';
import { expandImageGroupForIpeLayers } from '@/image-designer/adapters/ipeLayerAdapter';
import { saveGoodsCompositeResultsToImageSpace } from './saveGoodsCompositeToImageSpace';
import { hydrateTemplateCanvases } from './hydrateTemplateCanvases';

const DRAWABLE_RESOLVE_TIMEOUT_MS = 45000;

function withTimeout(promise, ms, label) {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error(`${label} 超时（${ms}ms）`)), ms);
    }),
  ]);
}

async function compressOneComposite(img, abortSignal) {
  if (abortSignal?.aborted) throw new Error(i18n.t('workbench:ipeUtilError.taskAborted'));
  if (!img.dataUrl?.startsWith('data:image/')) {
    return {
      imageId: img.imageId,
      groupIndex: img.groupIndex,
      templateId: img.templateId,
      dataUrl: img.dataUrl,
      width: img.width ?? null,
      height: img.height ?? null,
    };
  }
  try {
    const imgElement = new Image();
    await new Promise((resolve, reject) => {
      imgElement.onload = resolve;
      imgElement.onerror = reject;
      imgElement.src = img.dataUrl;
    });
    let compressedDataUrl = await compressImageForDatabase(
      img.dataUrl,
      imgElement.naturalWidth || 1000,
      imgElement.naturalHeight || 1000,
    );
    compressedDataUrl = await ensureImageSizeWithinLimit(
      compressedDataUrl,
      imgElement.naturalWidth || 1000,
      imgElement.naturalHeight || 1000,
    );
    return {
      imageId: img.imageId,
      groupIndex: img.groupIndex,
      templateId: img.templateId,
      dataUrl: compressedDataUrl,
      width: imgElement.naturalWidth || null,
      height: imgElement.naturalHeight || null,
    };
  } catch (error) {
    console.warn('[主图创建] 压缩合成图失败，使用原图', error);
    return {
      imageId: img.imageId,
      groupIndex: img.groupIndex,
      templateId: img.templateId,
      dataUrl: img.dataUrl,
      width: null,
      height: null,
    };
  }
}

/**
 * 多图套图：按 imageGroups 逐组处理（先完成上一组再处理下一组），并支持组级增量保存。
 */
function toGoodsImageUrlSnapshot(value) {
  if (value instanceof Map) return value;
  const map = new Map();
  if (value && typeof value === 'object') {
    Object.entries(value).forEach(([key, raw]) => {
      const url = raw != null ? String(raw).trim() : '';
      if (key && url) map.set(String(key), url);
    });
  }
  return map;
}

export async function runGoodsFormsSequentialComposite({
  effectiveGoodsForms,
  taskId,
  imageSpaceImages,
  goodsImageUrlSnapshot,
  fromCropTaskId,
  abortSignal,
  onProgress,
  skipCompletedGroups = false,
  billingNav = 'goods',
}) {
  const forms = Array.isArray(effectiveGoodsForms) ? effectiveGoodsForms : [];
  const spaceImages = Array.isArray(imageSpaceImages) ? imageSpaceImages : [];
  const urlSnapshot = toGoodsImageUrlSnapshot(goodsImageUrlSnapshot);
  let total = 0;
  forms.forEach((form) => {
    total += form.imageGroups?.length || 0;
  });

  const imageUrlMap = {};
  const allImageIds = new Set();

  forms.forEach((form) => {
    (form.imageGroups || []).forEach((group) => {
      Object.values(group || {}).forEach((imageRef) => {
        const normalizedId = normalizeGoodsImageRefToId(imageRef);
        if (!normalizedId) return;
        allImageIds.add(normalizedId);
        if (!imageUrlMap[normalizedId]) {
          const inline = inlineUrlFromGoodsImageRef(imageRef);
          if (inline) imageUrlMap[normalizedId] = inline;
        }
      });
    });
  });

  if (fromCropTaskId) {
    try {
      const { images: cropImages } = await cropImageAPI.getByTask(fromCropTaskId);
      (cropImages || []).forEach((img) => {
        const cropUrl = img.croppedImageUrl || img.url || '';
        if (!cropUrl) return;
        const cropImageId = String(img.imageId || '').trim();
        if (cropImageId && allImageIds.has(cropImageId)) {
          imageUrlMap[cropImageId] = cropUrl;
        }
        // 快捷联动常用 crop_${imageId}_${tileKey} / crop_original_${imageId}
        allImageIds.forEach((bridgeId) => {
          const id = String(bridgeId);
          if (imageUrlMap[id]) return;
          if (
            cropImageId
            && (id === `crop_original_${cropImageId}` || id.startsWith(`crop_${cropImageId}_`))
          ) {
            imageUrlMap[id] = cropUrl;
          }
        });
      });
    } catch (error) {
      console.warn('加载裁剪图片失败:', error);
    }
  }

  urlSnapshot.forEach((url, imageId) => {
    if (!allImageIds.has(imageId) || !url) return;
    imageUrlMap[imageId] = url;
  });

  const loadImageSpaceRecord = async (id) => {
    const local = spaceImages.find((img) => String(img.id) === String(id));
    if (local?.dataUrl && String(local.dataUrl).startsWith('data:image/')) {
      return local;
    }
    try {
      const res = await imageSpaceAPI.getById(id);
      return res?.image ?? res?.data ?? res ?? local ?? null;
    } catch {
      return local ?? null;
    }
  };

  const resolveRawStampUrl = async (imageId) => {
    if (imageUrlMap[imageId]) return;
    try {
      const image = spaceImages.find((img) => String(img.id) === String(imageId));
      if (image?.dataUrl && String(image.dataUrl).startsWith('data:image/')) {
        imageUrlMap[imageId] = String(image.dataUrl);
        return;
      }
      const fromSpace = await resolveUrlFromImageSpaceRecord(image);
      if (fromSpace) {
        imageUrlMap[imageId] = fromSpace;
        return;
      }
      const url = await getImageFromIndexedDB(imageId);
      if (url) {
        imageUrlMap[imageId] = url;
        return;
      }
      const rec = await loadImageSpaceRecord(imageId);
      const remote = await resolveUrlFromImageSpaceRecord(rec);
      if (remote) imageUrlMap[imageId] = remote;
    } catch (error) {
      console.warn(`无法加载图片 ${imageId}:`, error);
    }
  };

  const ensureDrawableForStampIds = async (stampIds) => {
    for (const imageId of stampIds) {
      if (abortSignal?.aborted) throw new Error(i18n.t('workbench:ipeUtilError.taskAborted'));
      await resolveRawStampUrl(imageId);
      const raw = imageUrlMap[imageId];
      if (!raw) continue;
      if (String(raw).startsWith('data:image/')) continue;
      const drawable = await withTimeout(
        resolveDrawableImageUrl(raw, {
          imageId,
          loadImageSpaceRecord,
        }),
        DRAWABLE_RESOLVE_TIMEOUT_MS,
        `印花图 ${imageId}`,
      ).catch((err) => {
        console.warn(`[主图创建] ${err.message || err}`);
        return '';
      });
      if (drawable?.startsWith?.('data:image/')) {
        imageUrlMap[imageId] = drawable;
      } else if (drawable) {
        // Fabric 渲染前还会签名；保留可用地址，禁止删光导致空印花占位（蓝框 ipelayerN）
        imageUrlMap[imageId] = drawable;
      } else {
        console.warn(
          `[主图创作] 印花图 ${imageId} 无法转为 data URL，保留原地址继续合成`,
          String(raw).slice(0, 120),
        );
      }
      await new Promise((r) => setTimeout(r, 0));
    }
  };

  const partialForms = forms.map((form) => ({
    ...form,
    compositeResults: skipCompletedGroups ? [...(form.compositeResults || [])] : [],
  }));
  const finalForms = [];
  let runningSuccess = 0;
  let runningFailed = 0;
  let savedIncrementally = false;

  if (skipCompletedGroups) {
    partialForms.forEach((form) => {
      const groupCount = form.imageGroups?.length || 0;
      for (let gi = 0; gi < groupCount; gi += 1) {
        const gr = (form.compositeResults || []).filter(
          (r) => r.groupIndex === gi && r.compositeUrl && String(r.compositeUrl).trim(),
        );
        if (gr.length > 0) runningSuccess += 1;
      }
    });
  }

  const emitProgress = (status = 'processing') => {
    if (typeof onProgress === 'function') {
      onProgress({
        total,
        successCount: runningSuccess,
        failedCount: runningFailed,
        status,
        partialGoodsForms: partialForms.map((f) => ({
          ...f,
          compositeResults: [...(f.compositeResults || [])],
        })),
      });
    }
  };

  emitProgress('processing');

  for (let formIndex = 0; formIndex < forms.length; formIndex += 1) {
    const form = forms[formIndex];
    if (abortSignal?.aborted) throw new Error(i18n.t('workbench:ipeUtilError.taskAborted'));

    const compositeResults = [...(partialForms[formIndex]?.compositeResults || [])];
    let templateData = form.templateData || {};
    try {
      const tplRes = await templateAPI.getById(form.templateId);
      if (tplRes?.template?.templateData) {
        const hydrated = await hydrateTemplateCanvases(tplRes.template);
        templateData = hydrated?.templateData || tplRes.template.templateData;
      }
    } catch (e) {
      console.warn(`[主图创建] 刷新模版 ${form.templateId} 失败，使用任务内嵌数据`, e);
      if (form.templateId && templateData?.canvasStorage === 'external') {
        try {
          const hydrated = await hydrateTemplateCanvases({
            id: form.templateId,
            templateData,
          });
          templateData = hydrated?.templateData || templateData;
        } catch {
          /* keep embedded */
        }
      }
    }

    const templateImages = Array.isArray(templateData?.images)
      ? templateData.images
      : Object.values(templateData?.images || {}).filter((img) => img && typeof img === 'object');
    const templateStampAreas = templateData?.stampAreas && typeof templateData.stampAreas === 'object'
      ? templateData.stampAreas
      : {};
    const slideUrlMap = await buildTemplateSlideUrlMap(
      form.templateId,
      templateImages,
      templateData,
    );

    for (let groupIndex = 0; groupIndex < (form.imageGroups?.length || 0); groupIndex += 1) {
      if (abortSignal?.aborted) throw new Error(i18n.t('workbench:ipeUtilError.taskAborted'));

      const existingGroupResults = compositeResults.filter((r) => r.groupIndex === groupIndex);
      if (
        skipCompletedGroups &&
        existingGroupResults.some((r) => r.compositeUrl && String(r.compositeUrl).trim())
      ) {
        partialForms[formIndex] = {
          ...form,
          templateData,
          compositeResults: [...compositeResults],
        };
        emitProgress('processing');
        await new Promise((r) => setTimeout(r, 0));
        continue;
      }

      const rawImageGroup = form.imageGroups[groupIndex] || {};
      let imageGroup = Object.entries(rawImageGroup).reduce((acc, [stampName, imageRef]) => {
        const composed = normalizeGoodsImageRefForCompose(imageRef);
        if (!composed) return acc;
        acc[stampName] = composed;
        // 确保 inline url 仍在 map 中，供 stampArea / ipelayer 注入
        const id = normalizeGoodsImageRefToId(composed);
        const inline = inlineUrlFromGoodsImageRef(composed)
          || (typeof composed === 'string'
            && (composed.startsWith('http') || composed.startsWith('/') || composed.startsWith('data:'))
            ? composed
            : '');
        if (id && inline && !imageUrlMap[id]) {
          imageUrlMap[id] = inline;
        }
        return acc;
      }, {});

      const stampIdsForGroup = [
        ...new Set(
          Object.values(imageGroup)
            .map((ref) => normalizeGoodsImageRefToId(ref))
            .filter(Boolean),
        ),
      ];
      await ensureDrawableForStampIds(stampIdsForGroup);

      for (const templateImage of templateImages) {
        if (abortSignal?.aborted) throw new Error(i18n.t('workbench:ipeUtilError.taskAborted'));

        const templateImageId = templateImage.id;

        // —— Fabric 全画布合成（ipelayer 图层名贴合 + stampArea 印花位）——
        if (slideUsesFabricComposite(templateImageId, templateData)) {
          const ipeLayers = getIpeLayersForSlide(templateImageId, templateData);
          const slideCanvas =
            templateData.slideCanvases?.[templateImageId]
            || (templateData.primaryImageId === templateImageId
              ? templateData.fabricCanvas
              : null);

          if (!slideCanvas) {
            console.warn(
              `[主图创建] 模版「${form.templateName}」页面 ${templateImageId} 缺少 fabricCanvas`,
            );
            let passThroughUrl = slideUrlMap[templateImageId]
              || templateImage.url
              || templateImage.src
              || templateImage.imageUrl
              || templateImage.viewUrl
              || templateImage.displayUrl
              || '';
            const existingPass = compositeResults.find(
              (r) => r.imageId === templateImageId && r.groupIndex === groupIndex,
            );
            if (!existingPass && passThroughUrl) {
              const kind = String(templateImage.role || '').trim();
              compositeResults.push({
                imageId: templateImageId,
                compositeUrl: passThroughUrl,
                groupIndex,
                passThrough: true,
                ...(kind ? { kind } : {}),
              });
            }
            continue;
          }

          const orderedIds = stampIdsForGroup;
          imageGroup = expandImageGroupForIpeLayers(imageGroup, ipeLayers, orderedIds);

          const stampAreasOnSlide = resolveStampAreasForSlide(templateImageId, templateStampAreas);
          if (stampAreasOnSlide.length > 0) {
            imageGroup = expandImageGroupForStampAreas(imageGroup, stampAreasOnSlide);
          }

          const requiredLayers = new Set(ipeLayers.map((l) => l.name));
          const requiredStamps = new Set(
            stampAreasOnSlide.map((s) => s?.name).filter(Boolean),
          );
          const requiredKeys = requiredLayers.size > 0 ? requiredLayers : requiredStamps;
          const imageGroupForSlots = imageGroup;
          const hasAllSlots = [...requiredKeys].every((name) => imageGroupForSlots[name]);
          if (requiredKeys.size > 0 && !hasAllSlots) {
            console.warn(
              `[主图创建] 模版「${form.templateName}」页面 ${templateImageId} 缺少贴合/印花：需要 ${[...requiredKeys].join('、')}，当前 ${Object.keys(imageGroup).join('、')}`,
            );
            continue;
          }

          try {
            const slideMeta = {
              id: templateImageId,
              name: templateImage.name || templateImageId,
              width: templateImage.width,
              height: templateImage.height,
              imageUrl: slideUrlMap[templateImageId]
                || templateImage.url
                || templateImage.src
                || templateImage.imageUrl
                || '',
              thumbUrl: templateImage.thumbUrl || '',
            };
            const { compositeUrl, fabricInstanceJson } = await compositeFabricSlideToDataUrl(
              slideCanvas,
              imageGroup,
              imageUrlMap,
              slideMeta,
            );
            const existingResult = compositeResults.find(
              (r) => r.imageId === templateImageId && r.groupIndex === groupIndex,
            );
            if (!existingResult && compositeUrl) {
              compositeResults.push({
                imageId: templateImageId,
                compositeUrl,
                groupIndex,
                composeEngine: 'fabric',
                fabricInstanceJson,
              });
            }
          } catch (error) {
            if (error.message === i18n.t('workbench:ipeUtilError.taskAborted')) throw error;
            console.error(
              `Fabric 合成失败 (模版: ${form.templateName}, 页面: ${templateImageId}):`,
              error,
            );
          }

          await new Promise((r) => setTimeout(r, 0));
          continue;
        }

        // —— 旧 stampAreas 底图贴印花 ——
        const stampAreas = resolveStampAreasForSlide(templateImageId, templateStampAreas);
        if (stampAreas.length === 0) {
          // 无印花区（常见于细节图）：仍透传模板底图，便于详情/刊登展示
          let passThroughUrl = slideUrlMap[templateImageId];
          if (!passThroughUrl) {
            passThroughUrl = templateImage.url
              || templateImage.src
              || templateImage.imageUrl
              || templateImage.viewUrl
              || templateImage.displayUrl
              || '';
          }
          if (!passThroughUrl) {
            try {
              passThroughUrl = await getImageFromIndexedDB(templateImageId);
            } catch {
              passThroughUrl = '';
            }
          }
          const existingPass = compositeResults.find(
            (r) => r.imageId === templateImageId && r.groupIndex === groupIndex,
          );
          if (!existingPass && passThroughUrl) {
            const kind = String(templateImage.role || '').trim();
            compositeResults.push({
              imageId: templateImageId,
              compositeUrl: passThroughUrl,
              groupIndex,
              passThrough: true,
              ...(kind ? { kind } : {}),
            });
          }
          continue;
        }

        imageGroup = expandImageGroupForStampAreas(imageGroup, stampAreas);

        const requiredStamps = new Set(stampAreas.map((s) => s.name));
        const imageGroupForStamps = imageGroup;
        const hasAllStamps = Array.from(requiredStamps).every((stampName) => imageGroupForStamps[stampName]);
        if (!hasAllStamps) {
          console.warn(
            `[主图创建] 模版「${form.templateName}」轮播 ${templateImageId} 缺少印花：需要 ${Array.from(requiredStamps).join('、')}，当前 ${Object.keys(imageGroup).join('、')}`,
          );
          continue;
        }

        let templateImageUrl = slideUrlMap[templateImageId];
        if (!templateImageUrl) {
          try {
            templateImageUrl = await getImageFromIndexedDB(templateImageId);
          } catch (error) {
            console.warn(`无法加载模版图片 ${templateImageId}:`, error);
          }
        }
        if (!templateImageUrl) {
          console.warn(
            `[主图创建] 模版「${form.templateName || form.templateId}」轮播图 ${templateImageId} 无可用地址`,
          );
          continue;
        }

        templateImageUrl = await resolveDrawableImageUrl(templateImageUrl, {
          imageId: templateImageId,
        });
        if (!templateImageUrl?.startsWith?.('data:image/')) {
          console.warn(`[主图创建] 无法解析轮播图 ${templateImageId} 为可绘制地址`);
          continue;
        }

        try {
          const compositeUrl = await compositeStampOntoTemplateDataUrl(
            templateImageUrl,
            { [templateImageId]: stampAreas },
            imageGroup,
            imageUrlMap,
            { templateData, templateImageId },
          );
          const existingResult = compositeResults.find(
            (r) => r.imageId === templateImageId && r.groupIndex === groupIndex,
          );
          if (!existingResult && compositeUrl) {
            compositeResults.push({
              imageId: templateImageId,
              compositeUrl,
              groupIndex,
            });
          }
        } catch (error) {
          if (error.message === i18n.t('workbench:ipeUtilError.taskAborted')) throw error;
          console.error(`贴图失败 (模版: ${form.templateName}, 图片: ${templateImageId}):`, error);
        }

        await new Promise((r) => setTimeout(r, 0));
      }

      const groupResults = compositeResults.filter((r) => r.groupIndex === groupIndex);
      if (groupResults.length > 0) {
        runningSuccess += 1;
      } else {
        runningFailed += 1;
      }

      partialForms[formIndex] = {
        ...form,
        templateData,
        compositeResults: [...compositeResults],
      };
      emitProgress('processing');
      // 让浏览器有机会绘制本组成品，再处理下一组
      await new Promise((r) => setTimeout(r, 48));

      if (groupResults.length > 0 && !abortSignal?.aborted) {
        const toSave = [];
        for (const result of groupResults) {
          if (!result.compositeUrl?.startsWith('data:image/')) continue;
          toSave.push({
            imageId: result.imageId,
            groupIndex: result.groupIndex,
            templateId: form.templateId,
            dataUrl: result.compositeUrl,
            width: null,
            height: null,
          });
        }
        const compressed = [];
        for (const img of toSave) {
          compressed.push(await compressOneComposite(img, abortSignal));
        }
        const valid = compressed.filter(
          (img) => img.imageId && img.groupIndex !== undefined && img.templateId && img.dataUrl,
        );
        if (valid.length > 0) {
          await compositeImageAPI.batchSave(taskId, valid, false, false);
          savedIncrementally = true;
          await saveGoodsCompositeResultsToImageSpace({
            taskId,
            items: valid,
            billingNav,
            templateName: form.templateName || form.templateId || '',
          });
        }
      }

      await new Promise((r) => setTimeout(r, 16));
    }

    finalForms.push(partialForms[formIndex]);
  }

  let successCount = 0;
  let failedCount = 0;
  finalForms.forEach((form) => {
    const templateImages = Array.isArray(form.templateData?.images)
      ? form.templateData.images
      : Object.values(form.templateData?.images || {}).filter((img) => img && typeof img === 'object');
    const templateImageCount = templateImages.length;
    const groupCount = form.imageGroups?.length || 0;
    const crs = form.compositeResults || [];
    for (let groupIndex = 0; groupIndex < groupCount; groupIndex += 1) {
      const groupResults = crs.filter((r) => r.groupIndex === groupIndex);
      if (groupResults.length === templateImageCount || groupResults.length > 0) {
        successCount += 1;
      } else {
        failedCount += 1;
      }
    }
  });

  let finalStatus = 'processing';
  if (successCount === total) finalStatus = 'success';
  else if (failedCount === total) finalStatus = 'failed';
  else if (successCount > 0) finalStatus = 'partial';
  else finalStatus = 'failed';

  const failureHint =
    finalStatus === 'failed' && successCount === 0
      ? (() => {
          const noSlides = finalForms.every(
            (f) => !(f.templateData?.images && f.templateData.images.length > 0),
          );
          if (noSlides) return i18n.t('workbench:ipeUtilError.templateNoSlides');
          const noStamps = finalForms.every((f) => {
            const areas = f.templateData?.stampAreas || {};
            return !Object.values(areas).some((arr) => Array.isArray(arr) && arr.length > 0);
          });
          if (noStamps) return i18n.t('workbench:ipeUtilError.templateNoStamps');
          return i18n.t('workbench:ipeUtilError.templateLoadFailed');
        })()
      : '';

  const finishedAt = format(new Date(), 'yyyy-MM-dd HH:mm');

  const formsWithReferences = finalForms.map((form) => {
    if (!form.compositeResults?.length) {
      return { ...form, compositeResults: [] };
    }
    const compositeReferences = form.compositeResults.map((result) => {
      if (result.compositeUrl?.startsWith('data:image/')) {
        const compositeImageId = `COMP-${taskId}-${form.templateId}-${result.groupIndex}-${result.imageId}`;
        return {
          imageId: result.imageId,
          compositeImageId,
          groupIndex: result.groupIndex,
        };
      }
      return result;
    });
    return { ...form, compositeResults: compositeReferences };
  });

  return {
    finalForms,
    formsWithReferences,
    total,
    successCount,
    failedCount,
    finalStatus,
    failureHint,
    finishedAt,
    savedIncrementally,
    partialForms,
  };
}
